/**
 * Scans feature components extracted from DashboardPage.js.
 *
 * Contains NetworkScanDatePicker, NetworkScanTimePicker,
 * NetworkScanManagerModalBody, WebApplicationScanManagerModalBody, and ScansPageContent.
 */

import React, { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { PageDataLoader } from '../../../components/BlueXLoader';
import { createPortal } from 'react-dom';
import { useNavigate, useSearchParams } from 'react-router-dom';
import '../scans.css';
import '../../assets/assets.css';
import '../../settings/connectors/connectors.css';
import ScanTable from './ScanTable/ScanTable';
import ScanNetworkOptions from './ScanNetworkOptions';
import ScanLoginOptions from './ScanLoginOptions';
import { scanLoginFieldsError, scanLoginPayload } from '../../settings/scan-network/ScanLoginFields';
import { proxyFieldsError, proxyPayload } from '../../settings/scan-network/ProxyFields';
import {
  deleteNetworkScanSchedule,
  rerunScan,
  haltNetworkScan,
  deleteNetworkScans,
  pauseNetworkScan,
  submitNetworkScanSchedule,
  submitWebApplicationScanSchedule,
  updateNetworkScanSchedule,
  updateWebApplicationScanSchedule,
} from '../../../services/scan.service';
import {
  getScanStatusWebSocketUrl,
  ensureAuthenticatedSession,
  toUserFacingErrorMessage,
} from '../../../services/_core';
import { getSites, listOrganizationsForSelector, getNetworkScannersForSite, getWebScannersForSite } from '../../../services/asset.service';
import { isOrgUuid, resolveOrgIdentity } from '../../../shared/orgIdentity';
import { buildScansPathWithFilter, parseScansFilterFromSearchParams, scanDetailPath } from '../../../routes/dashboardRoutes';
import {
  normalizeNetworkType,
  normalizeScansRow,
  upsertScanRowByIdentity,
  getScansLifecycleKind,
  isScanScheduleRecord,
  isScanInProgressOrFailedRun,
  networkLocationKey,
  getPreferredTimezoneDefault,
  getTimezoneOptions,
  getNetworkScanScheduleDefaults,
  toDateInputValue,
  toTimeInputValue,
  padTimeUnit,
  normalizeTimeString,
  normalizeDateString,
  formatTimePickerDisplay,
  formatDatePickerDisplay,
  getMonthShortLabel,
  buildDatePickerYears,
  daysInMonth,
  time24To12Parts,
  hour12PeriodTo24,
  TIME_PICKER_HOURS_12,
  TIME_PICKER_MINUTES,
  TIME_PICKER_PERIODS,
  DATE_PICKER_MONTH_NUMBERS,
  validateNetworkTargetsInput,
  validateWebUrlsInput,
  validatePortListInput,
  validateScheduleNameInput,
  sanitizeScheduleName,
} from '../scans.utils';
import { useNetworkScansQuery, useWebScansQuery } from '../hooks/useScansQueries';

const EMPTY_USER_PROFILE = {
  name: '',
  email: '',
  organizationName: '',
  role: '',
  timezone: getPreferredTimezoneDefault(),
  is_superuser: false,
  capabilities: null,
};

const SCANS_ORG_NONE_ID = '__none__';

function getScanOrgFilterId(row) {
  const name = String(normalizeScansRow(row)?.orgName || '').trim();
  return name || SCANS_ORG_NONE_ID;
}

function normalizeScheduleFrequency(raw) {
  const s = String(raw || '').trim().toLowerCase();
  if (!s || s.includes('one')) return 'one-off';
  if (s.includes('daily')) return 'daily';
  if (s.includes('fort')) return 'fortnightly';
  if (s.includes('week')) return 'weekly';
  if (s.includes('month')) return 'monthly';
  if (s.includes('quarter')) return 'quarterly';
  return 'one-off';
}

function parseScheduleStartParts(raw, userTimezone) {
  const defaults = getNetworkScanScheduleDefaults(userTimezone);
  const text = String(raw || '').trim();
  if (!text) return defaults;
  const iso = Date.parse(text);
  if (Number.isFinite(iso)) {
    const d = new Date(iso);
    return { startDate: toDateInputValue(d), startTime: toTimeInputValue(d) };
  }
  const m = text.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})\s+(\d{1,2}):(\d{2})(?::(\d{2}))?\s*(AM|PM)?/i);
  if (m) {
    const day = Number(m[1]);
    const mo = Number(m[2]);
    const y = Number(m[3]);
    let h = Number(m[4]);
    const min = Number(m[5]);
    const ap = String(m[7] || '').toUpperCase();
    if (ap === 'PM' && h < 12) h += 12;
    if (ap === 'AM' && h === 12) h = 0;
    const d = new Date(y, mo - 1, day, h, min, 0);
    return { startDate: toDateInputValue(d), startTime: toTimeInputValue(d) };
  }
  return defaults;
}

function mapScheduleRowToNetworkInitialValues(row, userTimezone) {
  const source = row?.__source && typeof row.__source === 'object' ? row.__source : row;
  const n = normalizeScansRow(source);
  const start = parseScheduleStartParts(n.nextRunAt || n.scanDate || source?.start_time, userTimezone);
  let tcp = '';
  let udp = '';
  try {
    const ports = typeof source?.custom_ports === 'string' ? JSON.parse(source.custom_ports || '{}') : (source?.custom_ports || {});
    tcp = String(ports?.tcp || source?.custom_tcp_ports || '').trim();
    udp = String(ports?.udp || source?.custom_udp_ports || '').trim();
  } catch {
    /* ignore */
  }
  const orgId = resolveOrgIdentity(source?.organization) || resolveOrgIdentity(source?.site?.organization) || String(source?.org_uu_id || source?.organization_id || '').trim();
  const siteId = String(
    n.siteUuid || source?.siteUuid || source?.site_uuid || source?.site_uu_id || source?.site?.uu_id || ''
  ).trim();
  const scannerId = String(
    n.scannerId
    || source?.scannerId
    || source?.network_scanner_uuid
    || source?.scanner_id
    || source?.setting_id
    || source?.scanner_openvas?.setting_id
    || ''
  ).trim();
  const loc = networkLocationKey(n);
  return {
    targets: String(n.target || '').trim(),
    exclude: String(source?.exclude_hosts || source?.exclude_ip || '').trim(),
    tcpPorts: tcp,
    udpPorts: udp,
    startDate: start.startDate,
    startTime: start.startTime,
    timezone: String(source?.schedule_timezone || source?.client_timezone || userTimezone || getPreferredTimezoneDefault()),
    frequency: normalizeScheduleFrequency(n.recurrence || source?.scan_freqency || source?.scan_frequency),
    scheduleName: String(n.scanName || source?.scan_name || source?.schedule_name || '').trim(),
    org: orgId,
    site: siteId,
    scanType: loc === 'onprem' ? 'on-premise' : 'perimeter',
    scanner: scannerId,
  };
}

function mapScheduleRowToWebInitialValues(row, userTimezone) {
  const source = row?.__source && typeof row.__source === 'object' ? row.__source : row;
  const n = normalizeScansRow(source);
  const start = parseScheduleStartParts(n.nextRunAt || n.scanDate || source?.start_time, userTimezone);
  const orgId = resolveOrgIdentity(source?.organization) || String(source?.org_uu_id || '').trim();
  const siteId = String(
    n.siteUuid || source?.siteUuid || source?.site_uuid || source?.site_uu_id || source?.site?.uu_id || ''
  ).trim();
  const scannerId = String(
    n.scannerId
    || source?.scannerId
    || source?.scanner_id
    || source?.setting_id
    || source?.web_scanner_uuid
    || source?.scanner_zap?.setting_id
    || ''
  ).trim();
  return {
    urls: String(n.target || source?.url || '').trim(),
    startDate: start.startDate,
    startTime: start.startTime,
    timezone: String(source?.schedule_timezone || source?.client_timezone || userTimezone || getPreferredTimezoneDefault()),
    frequency: normalizeScheduleFrequency(n.recurrence || source?.scan_freqency || source?.periodic_task_value),
    scheduleName: String(n.scanName || source?.scan_name || source?.schedule_name || '').trim(),
    org: orgId,
    site: siteId,
    scanner: scannerId,
  };
}

// Re-export helpers that DashboardPage.js still needs
export {
  normalizeScansRow,
  normalizeNetworkType,
  getPreferredTimezoneDefault,
  getTimezoneOptions,
  getNetworkScanScheduleDefaults,
  toDateInputValue,
  toTimeInputValue,
  mapScheduleRowToNetworkInitialValues,
  mapScheduleRowToWebInitialValues,
};

// --- Date/time picker components ---

function NetworkScanDatePicker({ id, value, onChange, disabled, theme = 'dark' }) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef(null);
  const panelRef = useRef(null);
  const [panelPos, setPanelPos] = useState({ top: 0, left: 0, minWidth: 312 });

  const canon = normalizeDateString(value);
  const parts = useMemo(() => {
    const [y, mo, d] = canon.split('-').map((x) => parseInt(x, 10));
    return { y, m: mo, d };
  }, [canon]);

  const years = useMemo(() => {
    const base = buildDatePickerYears();
    const y = parts.y;
    if (Number.isFinite(y) && (y < base[0] || y > base[base.length - 1])) {
      return [...base, y].sort((a, b) => a - b);
    }
    return base;
  }, [parts.y]);
  const dayNumbers = useMemo(
    () => Array.from({ length: daysInMonth(parts.y, parts.m) }, (_, i) => i + 1),
    [parts.y, parts.m]
  );

  const reposition = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const margin = 10;
    const minWidth = Math.max(Math.round(r.width), 312);
    let left = r.left;
    const vw = window.innerWidth;
    if (left + minWidth > vw - margin) {
      left = Math.max(margin, vw - minWidth - margin);
    }
    setPanelPos({
      top: r.bottom + 8,
      left,
      minWidth
    });
  }, []);

  useLayoutEffect(() => {
    if (!open) return;
    reposition();
    const onReposition = () => reposition();
    window.addEventListener('resize', onReposition);
    window.addEventListener('scroll', onReposition, true);
    return () => {
      window.removeEventListener('resize', onReposition);
      window.removeEventListener('scroll', onReposition, true);
    };
  }, [open, reposition]);

  useLayoutEffect(() => {
    if (!open || !panelRef.current) return;
    panelRef.current.querySelectorAll('[data-date-picker-selected="1"]').forEach((node) => {
      node.scrollIntoView({ block: 'center' });
    });
  }, [open, canon, parts.y, parts.m, parts.d]);

  useEffect(() => {
    if (!open) return;
    const onDocMouseDown = (e) => {
      if (triggerRef.current?.contains(e.target) || panelRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  const setYMD = useCallback(
    (y, m, d) => {
      const dim = daysInMonth(y, m);
      const dd = Math.min(Math.max(1, d), dim);
      onChange(`${y}-${padTimeUnit(m)}-${padTimeUnit(dd)}`);
    },
    [onChange]
  );

  const onPickMonth = (mPick) => setYMD(parts.y, mPick, parts.d);
  const onPickDay = (dPick) => setYMD(parts.y, parts.m, dPick);
  const onPickYear = (yPick) => setYMD(yPick, parts.m, parts.d);

  const panelThemeClass = theme === 'light' ? ' network-scan-manager-time-picker-panel--light' : '';

  const panelEl =
    open && typeof document !== 'undefined' ? (
      <div
        ref={panelRef}
        className={`network-scan-manager-time-picker-panel${panelThemeClass}`}
        role="listbox"
        aria-label="Select date"
        style={{
          position: 'fixed',
          top: panelPos.top,
          left: panelPos.left,
          minWidth: panelPos.minWidth,
          zIndex: 100000
        }}
      >
        <div className="network-scan-manager-time-picker-cols">
          <div className="network-scan-manager-time-picker-col network-scan-manager-time-picker-col--month">
            <div className="network-scan-manager-time-picker-col-header">Month</div>
            <div className="network-scan-manager-time-picker-col-scroll">
              {DATE_PICKER_MONTH_NUMBERS.map((mo) => {
                const sel = mo === parts.m;
                return (
                  <button
                    key={mo}
                    type="button"
                    role="option"
                    aria-selected={sel}
                    data-date-picker-selected={sel ? '1' : '0'}
                    className={`network-scan-manager-time-picker-cell network-scan-manager-time-picker-cell--month${
                      sel ? ' is-selected' : ''
                    }`}
                    onClick={() => onPickMonth(mo)}
                  >
                    {getMonthShortLabel(mo)}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="network-scan-manager-time-picker-col network-scan-manager-time-picker-col--day">
            <div className="network-scan-manager-time-picker-col-header">Day</div>
            <div className="network-scan-manager-time-picker-col-scroll">
              {dayNumbers.map((day) => {
                const sel = day === parts.d;
                return (
                  <button
                    key={day}
                    type="button"
                    role="option"
                    aria-selected={sel}
                    data-date-picker-selected={sel ? '1' : '0'}
                    className={`network-scan-manager-time-picker-cell${sel ? ' is-selected' : ''}`}
                    onClick={() => onPickDay(day)}
                  >
                    {padTimeUnit(day)}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="network-scan-manager-time-picker-col network-scan-manager-time-picker-col--year">
            <div className="network-scan-manager-time-picker-col-header">Year</div>
            <div className="network-scan-manager-time-picker-col-scroll">
              {years.map((yr) => {
                const sel = yr === parts.y;
                return (
                  <button
                    key={yr}
                    type="button"
                    role="option"
                    aria-selected={sel}
                    data-date-picker-selected={sel ? '1' : '0'}
                    className={`network-scan-manager-time-picker-cell${sel ? ' is-selected' : ''}`}
                    onClick={() => onPickYear(yr)}
                  >
                    {yr}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    ) : null;

  return (
    <div className="network-scan-manager-time-picker">
      <button
        ref={triggerRef}
        id={id}
        type="button"
        disabled={disabled}
        className="network-scan-manager-time-picker-trigger ghost-exposure-sys-input"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={`Select date, ${formatDatePickerDisplay(value)}`}
        onClick={() => setOpen((v) => !v)}
      >
        <span className="network-scan-manager-time-picker-trigger-value">{formatDatePickerDisplay(value)}</span>
        <span className="network-scan-manager-time-picker-trigger-icon" aria-hidden="true">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M4 10h16m-8-3V4M7 7V4m10 3V4M5 20h14a1 1 0 0 0 1-1V7a1 1 0 0 0-1-1H5a1 1 0 0 0-1 1v12a1 1 0 0 0 1 1Zm3-7h.01v.01H8V13Zm4 0h.01v.01H12V13Zm4 0h.01v.01H16V13Zm-8 4h.01v.01H8V17Zm4 0h.01v.01H12V17Zm4 0h.01v.01H16V17Z"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>
      </button>
      {panelEl ? createPortal(panelEl, document.body) : null}
    </div>
  );
}

function NetworkScanTimePicker({ id, value, onChange, disabled, theme = 'dark' }) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef(null);
  const panelRef = useRef(null);
  const [panelPos, setPanelPos] = useState({ top: 0, left: 0, minWidth: 296 });

  const { h12, minute, period } = useMemo(() => {
    const t = normalizeTimeString(value);
    const [h24, m] = t.split(':').map((x) => parseInt(x, 10));
    return time24To12Parts(h24, m);
  }, [value]);

  const reposition = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const margin = 10;
    const minWidth = Math.max(Math.round(r.width), 296);
    let left = r.left;
    const vw = window.innerWidth;
    if (left + minWidth > vw - margin) {
      left = Math.max(margin, vw - minWidth - margin);
    }
    setPanelPos({
      top: r.bottom + 8,
      left,
      minWidth
    });
  }, []);

  useLayoutEffect(() => {
    if (!open) return;
    reposition();
    const onReposition = () => reposition();
    window.addEventListener('resize', onReposition);
    window.addEventListener('scroll', onReposition, true);
    return () => {
      window.removeEventListener('resize', onReposition);
      window.removeEventListener('scroll', onReposition, true);
    };
  }, [open, reposition]);

  useLayoutEffect(() => {
    if (!open || !panelRef.current) return;
    panelRef.current.querySelectorAll('[data-time-picker-selected="1"]').forEach((node) => {
      node.scrollIntoView({ block: 'center' });
    });
  }, [open, value, h12, minute, period]);

  useEffect(() => {
    if (!open) return;
    const onDocMouseDown = (e) => {
      if (triggerRef.current?.contains(e.target) || panelRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  const setTime = useCallback(
    (h24Next, mNext) => {
      onChange(`${padTimeUnit(h24Next)}:${padTimeUnit(mNext)}`);
    },
    [onChange]
  );

  const onPickHour = (hPick) => {
    setTime(hour12PeriodTo24(hPick, period), minute);
  };
  const onPickMinute = (mPick) => {
    setTime(hour12PeriodTo24(h12, period), mPick);
  };
  const onPickPeriod = (per) => {
    setTime(hour12PeriodTo24(h12, per), minute);
  };

  const panelThemeClass = theme === 'light' ? ' network-scan-manager-time-picker-panel--light' : '';

  const panelEl =
    open && typeof document !== 'undefined' ? (
      <div
        ref={panelRef}
        className={`network-scan-manager-time-picker-panel${panelThemeClass}`}
        role="listbox"
        aria-label="Select time"
        style={{
          position: 'fixed',
          top: panelPos.top,
          left: panelPos.left,
          minWidth: panelPos.minWidth,
          zIndex: 100000
        }}
      >
        <div className="network-scan-manager-time-picker-cols">
          <div className="network-scan-manager-time-picker-col">
            <div className="network-scan-manager-time-picker-col-header">Hour</div>
            <div className="network-scan-manager-time-picker-col-scroll">
              {TIME_PICKER_HOURS_12.map((h) => {
                const sel = h === h12;
                return (
                  <button
                    key={h}
                    type="button"
                    role="option"
                    aria-selected={sel}
                    data-time-picker-selected={sel ? '1' : '0'}
                    className={`network-scan-manager-time-picker-cell${sel ? ' is-selected' : ''}`}
                    onClick={() => onPickHour(h)}
                  >
                    {padTimeUnit(h)}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="network-scan-manager-time-picker-col">
            <div className="network-scan-manager-time-picker-col-header">Min</div>
            <div className="network-scan-manager-time-picker-col-scroll">
              {TIME_PICKER_MINUTES.map((mi) => {
                const sel = mi === minute;
                return (
                  <button
                    key={mi}
                    type="button"
                    role="option"
                    aria-selected={sel}
                    data-time-picker-selected={sel ? '1' : '0'}
                    className={`network-scan-manager-time-picker-cell${sel ? ' is-selected' : ''}`}
                    onClick={() => onPickMinute(mi)}
                  >
                    {padTimeUnit(mi)}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="network-scan-manager-time-picker-col">
            <div className="network-scan-manager-time-picker-col-header">AM/PM</div>
            <div className="network-scan-manager-time-picker-col-scroll">
              {TIME_PICKER_PERIODS.map((p) => {
                const sel = p === period;
                return (
                  <button
                    key={p}
                    type="button"
                    role="option"
                    aria-selected={sel}
                    data-time-picker-selected={sel ? '1' : '0'}
                    className={`network-scan-manager-time-picker-cell${sel ? ' is-selected' : ''}`}
                    onClick={() => onPickPeriod(p)}
                  >
                    {p}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    ) : null;

  return (
    <div className="network-scan-manager-time-picker">
      <button
        ref={triggerRef}
        id={id}
        type="button"
        disabled={disabled}
        className="network-scan-manager-time-picker-trigger ghost-exposure-sys-input"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={`Select time, ${formatTimePickerDisplay(value)}`}
        onClick={() => setOpen((v) => !v)}
      >
        <span className="network-scan-manager-time-picker-trigger-value">{formatTimePickerDisplay(value)}</span>
        <span className="network-scan-manager-time-picker-trigger-icon" aria-hidden="true">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M12 8v4l3 3m6-3a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>
      </button>
      {panelEl ? createPortal(panelEl, document.body) : null}
    </div>
  );
}

// --- Scan manager modal bodies ---

const PERIMETER_POOL_OPTION = {
  value: 'perimeter-pool',
  label: 'Public perimeter pool (automatic)',
  isPool: true,
};

const NetworkScanManagerModalBody = ({
  onBack,
  onClose,
  onSubmit,
  showBack = true,
  timePickerTheme = 'dark',
  userTimezone = getPreferredTimezoneDefault(),
  orgQuery = null,
  mode = 'create',
  initialValues = null,
  title = null,
}) => {
  const isEditMode = mode === 'edit';
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [orgOptions, setOrgOptions] = useState([]);
  const [orgOptionsLoading, setOrgOptionsLoading] = useState(false);
  const [siteOptions, setSiteOptions] = useState([]);
  const [siteOptionsLoading, setSiteOptionsLoading] = useState(false);
  const [scannerOptions, setScannerOptions] = useState([]);
  const [scannerOptionsLoading, setScannerOptionsLoading] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [optionsError, setOptionsError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [form, setForm] = useState(() => {
    const { startDate, startTime } = getNetworkScanScheduleDefaults(userTimezone);
    const defaults = {
      targets: '',
      exclude: '',
      tcpPorts: '',
      udpPorts: '',
      startDate,
      startTime,
      timezone: String(userTimezone || getPreferredTimezoneDefault()),
      frequency: 'one-off',
      scheduleName: '',
      org: String(orgQuery?.org_id || '').trim(),
      site: '',
      scanType: 'perimeter',
      scanner: '',
    };
    const merged = { ...defaults, ...(initialValues || {}) };
    return {
      ...merged,
      scheduleName: sanitizeScheduleName(merged.scheduleName),
      targets: String(merged.targets ?? '').replace(/\0/g, ''),
      exclude: String(merged.exclude ?? '').replace(/\0/g, ''),
      tcpPorts: String(merged.tcpPorts ?? '').replace(/\0/g, ''),
      udpPorts: String(merged.udpPorts ?? '').replace(/\0/g, ''),
    };
  });

  const scopeQuery = useMemo(() => {
    const orgId = String(form.org || orgQuery?.org_id || '').trim();
    return orgId ? { org_id: orgId } : null;
  }, [form.org, orgQuery]);

  useEffect(() => {
    let mounted = true;
    setOrgOptionsLoading(true);
    setOptionsError('');
    listOrganizationsForSelector()
      .then((rows) => {
        if (!mounted) return;
        const list = Array.isArray(rows) ? rows : [];
        const normalized = list
          .map((item) => ({
            value: resolveOrgIdentity(item),
            label: String(item?.name || item?.org_name || '').trim() || 'Unnamed organization',
          }))
          .filter((item) => item.value);
        setOrgOptions(normalized);
        if (!normalized.length) {
          setOptionsError('No organizations are available for your account.');
        }
        setForm((f) => {
          if (isEditMode && f.org) return f;
          if (f.org && normalized.some((o) => o.value === f.org)) return f;
          const preferred = String(orgQuery?.org_id || '').trim();
          if (preferred && normalized.some((o) => o.value === preferred)) {
            return { ...f, org: preferred, site: '', scanner: '' };
          }
          if (normalized.length === 1) {
            return { ...f, org: normalized[0].value, site: '', scanner: '' };
          }
          return { ...f, org: '', site: '', scanner: '' };
        });
      })
      .catch((err) => {
        if (!mounted) return;
        setOrgOptions([]);
        setOptionsError(
          toUserFacingErrorMessage(err?.message, 'Unable to load organizations. Please try again.')
        );
      })
      .finally(() => {
        if (!mounted) return;
        setOrgOptionsLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, [isEditMode, orgQuery?.org_id]);

  useEffect(() => {
    // `org_uu_id` must be a UUID; never fall back to the integer pk.
    const orgUuid = isOrgUuid(form.org) ? String(form.org).trim() : '';
    if (!orgUuid) {
      setSiteOptions([]);
      setSiteOptionsLoading(false);
      setForm((f) => (isEditMode ? f : (f.site || f.scanner ? { ...f, site: '', scanner: '' } : f)));
      return;
    }

    let mounted = true;
    setSiteOptionsLoading(true);
    setOptionsError('');
    getSites(null, { org_uu_id: orgUuid })
      .then((rows) => {
        if (!mounted) return;
        const normalized = (Array.isArray(rows) ? rows : []).map((item) => ({
          value: item?.uu_id || item?.site_uuid || item?.id || '',
          label: item?.site_name || item?.name || item?.site || 'Unnamed site',
        })).filter((item) => item.value);
        setSiteOptions(normalized);
        setForm((f) => {
          if (isEditMode && f.site) return f;
          if (normalized.some((s) => s.value === f.site)) return f;
          if (!normalized.length) return { ...f, site: '', scanner: '' };
          return { ...f, site: normalized[0].value, scanner: '' };
        });
      })
      .catch((err) => {
        if (!mounted) return;
        setSiteOptions([]);
        setForm((f) => (isEditMode ? f : { ...f, site: '', scanner: '' }));
        setOptionsError(
          toUserFacingErrorMessage(err?.message, 'Unable to load sites for this organization.')
        );
      })
      .finally(() => {
        if (!mounted) return;
        setSiteOptionsLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, [form.org, isEditMode]);

  useEffect(() => {
    const siteUuid = String(form.site || '').trim();
    if (!siteUuid) {
      setScannerOptions([]);
      return;
    }
    let mounted = true;
    setScannerOptionsLoading(true);
    setOptionsError('');
    const normalizedScanType = form.scanType === 'on-premise' ? 'on_premise' : form.scanType;
    getNetworkScannersForSite(siteUuid, normalizedScanType, null, scopeQuery)
      .then((rows) => {
        if (!mounted) return;
        const normalized = (Array.isArray(rows) ? rows : [])
          .map((item) => ({
            value: item?.setting_id || item?.id || '',
            label: item?.scanner_vanity_name || item?.scanner_name || item?.name || 'Unnamed scanner',
            isPool: item?.is_pool === true,
          }))
          .filter((item) => item.value);
        // Perimeter always has the public pool; never leave the user with an empty picker.
        if (normalizedScanType === 'perimeter' && normalized.length === 0) {
          normalized.push(PERIMETER_POOL_OPTION);
        }
        setScannerOptions(normalized);
        setForm((f) => ({
          ...f,
          scanner:
            isEditMode && f.scanner
              ? f.scanner
              : normalized.some((s) => s.value === f.scanner)
                ? f.scanner
                : normalized[0]?.value || '',
        }));
      })
      .catch((err) => {
        if (!mounted) return;
        setScannerOptions([]);
        setForm((f) => ({ ...f, scanner: '' }));
        setOptionsError(
          toUserFacingErrorMessage(err?.message, 'Unable to load scanners for this site.')
        );
      })
      .finally(() => {
        if (!mounted) return;
        setScannerOptionsLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, [form.site, form.scanType, isEditMode, scopeQuery]);

  const patch = (partial) => setForm((f) => ({ ...f, ...partial }));
  // Perimeter scans go to the load-balanced public pool unless the org has dedicated scanners.
  const perimeterPoolOnly =
    form.scanType === 'perimeter'
    && !scannerOptionsLoading
    && scannerOptions.length === 1
    && scannerOptions[0].isPool;

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setSubmitError('');
    if (isSubmitting) return;

    if (!String(form.org || '').trim()) {
      setSubmitError('Please select an organization.');
      return;
    }
    if (!String(form.site || '').trim()) {
      setSubmitError('Please select a site.');
      return;
    }
    if (!String(form.scanner || '').trim()) {
      setSubmitError('Please select a scanner.');
      return;
    }

    const targetValidation = validateNetworkTargetsInput(form.targets);
    if (!targetValidation.valid) {
      const badPart = targetValidation.invalidEntry ? ` Invalid entry: ${targetValidation.invalidEntry}` : '';
      setSubmitError(`${targetValidation.message}${badPart}`);
      return;
    }

    let excludeSanitized = '';
    if (String(form.exclude || '').trim()) {
      const excludeValidation = validateNetworkTargetsInput(form.exclude);
      if (!excludeValidation.valid) {
        const badPart = excludeValidation.invalidEntry ? ` Invalid entry: ${excludeValidation.invalidEntry}` : '';
        setSubmitError(`Exclude targets are invalid. ${excludeValidation.message}${badPart}`);
        return;
      }
      excludeSanitized = excludeValidation.sanitized || '';
    }

    const tcpValidation = validatePortListInput(form.tcpPorts, { label: 'TCP ports' });
    if (!tcpValidation.valid) {
      setSubmitError(tcpValidation.message);
      return;
    }
    const udpValidation = validatePortListInput(form.udpPorts, { label: 'UDP ports' });
    if (!udpValidation.valid) {
      setSubmitError(udpValidation.message);
      return;
    }

    const freq = String(form.frequency || 'one-off').trim().toLowerCase();
    const nameValidation = validateScheduleNameInput(form.scheduleName, { required: freq !== 'one-off' });
    if (!nameValidation.valid) {
      setSubmitError(nameValidation.message);
      return;
    }

    if (!normalizeDateString(String(form.startDate || '').trim())) {
      setSubmitError('Please select a valid start date.');
      return;
    }
    if (!normalizeTimeString(String(form.startTime || '').trim())) {
      setSubmitError('Please select a valid start time.');
      return;
    }

    const now = new Date();
    const datePart = normalizeDateString(String(form.startDate || '').trim() || toDateInputValue(now));
    const timePart = normalizeTimeString(String(form.startTime || '').trim() || toTimeInputValue(now));
    const payload = {
      ...form,
      targets: targetValidation.sanitized || String(form.targets || '').replace(/\0/g, '').trim(),
      exclude: excludeSanitized,
      tcpPorts: tcpValidation.sanitized || String(form.tcpPorts || '').trim(),
      udpPorts: udpValidation.sanitized || String(form.udpPorts || '').trim(),
      scheduleName: nameValidation.sanitized,
      startDate: datePart,
      startTime: timePart,
      startDateTime: `${datePart}T${timePart}`
    };

    setIsSubmitting(true);
    try {
      await onSubmit?.(payload);
    } catch (err) {
      setSubmitError(toUserFacingErrorMessage(err?.message, 'Failed to schedule network scan.'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form className="network-scan-manager network-scan-manager--cards" onSubmit={handleFormSubmit} noValidate>
      <div className={`network-scan-manager-top${showBack ? '' : ' network-scan-manager-top--no-back'}`}>
        {showBack ? (
          <button type="button" className="network-scan-manager-back" onClick={onBack} aria-label="Back">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </button>
        ) : null}
        <h2 id="network-scan-manager-title" className="ghost-cve-section-title network-scan-manager-title">
          {title || (isEditMode ? 'Edit Network Schedule' : 'Network Scan Manager')}
        </h2>
        <button type="button" className="new-scan-modal-close" aria-label="Close" onClick={onClose}>
          ✕
        </button>
      </div>

      <div className="network-scan-manager-body">
        <section className="settings-modal-card" aria-label="Targets">
          <div className="settings-modal-card-body">
            <div className="network-scan-manager-field">
              <label className="ghost-exposure-sys-field-label network-scan-manager-label" htmlFor="nsm-targets">
                Scan IP&apos;s/FQDNs
              </label>
              <textarea
                id="nsm-targets"
                className="ghost-exposure-sys-input network-scan-manager-textarea"
                rows={4}
                placeholder="Include IPs/FQDNs. E.g. 192.168.1.1-192.168.1.2, www.test.com"
                value={form.targets}
                onChange={(e) => patch({ targets: e.target.value })}
                autoComplete="off"
              />
            </div>

            <button
              type="button"
              id="nsm-advanced-toggle"
              className={`network-scan-manager-advanced-toggle${advancedOpen ? ' is-open' : ''}`}
              onClick={() => setAdvancedOpen((o) => !o)}
              aria-expanded={advancedOpen}
              aria-controls="nsm-advanced-section"
            >
              Advanced Settings
              <svg
                className="network-scan-manager-advanced-chevron"
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </button>

            <div
              id="nsm-advanced-section"
              role="region"
              aria-labelledby="nsm-advanced-toggle"
              aria-hidden={!advancedOpen}
              inert={!advancedOpen ? true : undefined}
              className={`network-scan-manager-advanced-wrap${advancedOpen ? ' is-expanded' : ''}`}
            >
              <div className="network-scan-manager-advanced-sizer">
                <div className="network-scan-manager-advanced">
                  <div className="network-scan-manager-field">
                    <label className="ghost-exposure-sys-field-label network-scan-manager-label" htmlFor="nsm-exclude">
                      Exclude IP&apos;s/FQDNs
                    </label>
                    <textarea
                      id="nsm-exclude"
                      className="ghost-exposure-sys-input network-scan-manager-textarea"
                      rows={3}
                      placeholder="Exclude IPs/FQDNs. E.g. 192.168.1.1-192.168.1.2, www.test.com"
                      value={form.exclude}
                      onChange={(e) => patch({ exclude: e.target.value })}
                      autoComplete="off"
                    />
                  </div>
                  <div className="network-scan-manager-ports">
                    <div className="network-scan-manager-field">
                      <label className="ghost-exposure-sys-field-label network-scan-manager-label" htmlFor="nsm-tcp">
                        TCP
                      </label>
                      <input
                        id="nsm-tcp"
                        type="text"
                        className="ghost-exposure-sys-input network-scan-manager-input"
                        placeholder="TCP ports. E.g. 80, 443, 1-65535"
                        value={form.tcpPorts}
                        onChange={(e) => patch({ tcpPorts: e.target.value })}
                        autoComplete="off"
                      />
                    </div>
                    <div className="network-scan-manager-field">
                      <label className="ghost-exposure-sys-field-label network-scan-manager-label" htmlFor="nsm-udp">
                        UDP
                      </label>
                      <input
                        id="nsm-udp"
                        type="text"
                        className="ghost-exposure-sys-input network-scan-manager-input"
                        placeholder="UDP ports. E.g. 80, 443, 1-65535"
                        value={form.udpPorts}
                        onChange={(e) => patch({ udpPorts: e.target.value })}
                        autoComplete="off"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="settings-modal-card" aria-label="Schedule">
          <div className="settings-modal-card-body">
            <div className="network-scan-manager-meta-row">
              <div className="network-scan-manager-field">
                <label className="ghost-exposure-sys-field-label network-scan-manager-label" htmlFor="nsm-start">
                  {form.frequency === 'one-off' ? 'Start date & time' : 'Recurring schedule'}
                </label>
                {form.frequency === 'daily' && (
                  <span className="settings-modal-card-hint network-scan-manager-freq-hint">
                    Scan will repeat every day at the selected time.
                  </span>
                )}
                {form.frequency === 'weekly' && (
                  <span className="settings-modal-card-hint network-scan-manager-freq-hint">
                    Scan will repeat every week on the selected weekday at the selected time.
                  </span>
                )}
                {form.frequency === 'fortnightly' && (
                  <span className="settings-modal-card-hint network-scan-manager-freq-hint">
                    Scan will repeat twice a month (about every 14 days) around the selected day and time.
                  </span>
                )}
                {form.frequency === 'monthly' && (
                  <span className="settings-modal-card-hint network-scan-manager-freq-hint">
                    Scan will repeat on day {form.startDate ? new Date(form.startDate + 'T00:00').getDate() : ''} of every month at the selected time.
                  </span>
                )}
                {form.frequency === 'quarterly' && (
                  <span className="settings-modal-card-hint network-scan-manager-freq-hint">
                    Scan will repeat every 3 months on the selected day and time.
                  </span>
                )}
                <div className="network-scan-manager-date-time-grid">
                  <div className="network-scan-manager-date-picker-wrap">
                    <div className="network-scan-manager-field-label-row">
                      <span className="network-scan-manager-time-field-label">
                        {form.frequency === 'one-off' ? 'Select date' : 'Select recurring day'}
                      </span>
                      <button
                        type="button"
                        className="network-scan-manager-inline-quick-action"
                        onClick={() => patch({ startDate: toDateInputValue(new Date()) })}
                      >
                        Today
                      </button>
                    </div>
                    <NetworkScanDatePicker
                      id="nsm-start"
                      value={form.startDate}
                      onChange={(next) => patch({ startDate: next })}
                      theme={timePickerTheme}
                    />
                  </div>
                  <div className="network-scan-manager-time-wrap">
                    <div className="network-scan-manager-field-label-row">
                      <span className="network-scan-manager-time-field-label">
                        {form.frequency === 'one-off' ? 'Select time' : 'Select recurring time'}
                      </span>
                      <button
                        type="button"
                        className="network-scan-manager-inline-quick-action"
                        onClick={() => patch({ startTime: toTimeInputValue(new Date()) })}
                      >
                        Now
                      </button>
                    </div>
                    <NetworkScanTimePicker
                      id="nsm-start-time"
                      value={form.startTime}
                      onChange={(next) => patch({ startTime: next })}
                      theme={timePickerTheme}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="network-scan-manager-field">
              <label className="ghost-exposure-sys-field-label network-scan-manager-label" htmlFor="nsm-frequency">
                Scan frequency
              </label>
              <select
                id="nsm-frequency"
                className="ghost-exposure-sys-input network-scan-manager-select"
                value={form.frequency}
                onChange={(e) => {
                  const v = e.target.value;
                  patch(v === 'one-off' ? { frequency: v, scheduleName: '' } : { frequency: v });
                }}
              >
                <option value="one-off">One-Off</option>
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
                <option value="fortnightly">Fortnightly</option>
                <option value="monthly">Monthly</option>
                <option value="quarterly">Quarterly</option>
              </select>
            </div>

            {form.frequency !== 'one-off' ? (
              <div className="network-scan-manager-field">
                <label className="ghost-exposure-sys-field-label network-scan-manager-label" htmlFor="nsm-schedule-name">
                  Schedule name
                </label>
                <input
                  id="nsm-schedule-name"
                  type="text"
                  className="ghost-exposure-sys-input network-scan-manager-input"
                  value={form.scheduleName}
                  onChange={(e) => patch({ scheduleName: e.target.value })}
                  autoComplete="off"
                />
              </div>
            ) : null}
          </div>
        </section>

        <section className="settings-modal-card" aria-label="Scope and scanner">
          <div className="settings-modal-card-body">
            <div className="network-scan-manager-field">
              <label className="ghost-exposure-sys-field-label network-scan-manager-label" htmlFor="nsm-org">
                Organization
              </label>
              <select
                id="nsm-org"
                className="ghost-exposure-sys-input network-scan-manager-select"
                value={form.org}
                onChange={(e) => patch({ org: e.target.value, site: '', scanner: '' })}
                disabled={orgOptionsLoading || isSubmitting || isEditMode}
              >
                <option value="">
                  {orgOptionsLoading ? 'Loading organizations…' : 'Please select an organization'}
                </option>
                {orgOptions.map((org) => (
                  <option key={org.value} value={org.value}>
                    {org.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="network-scan-manager-field">
              <label className="ghost-exposure-sys-field-label network-scan-manager-label" htmlFor="nsm-site">
                Site
              </label>
              <select
                id="nsm-site"
                className="ghost-exposure-sys-input network-scan-manager-select"
                value={form.site}
                onChange={(e) => patch({ site: e.target.value, scanner: '' })}
                disabled={siteOptionsLoading || !form.org || isSubmitting || isEditMode}
              >
                <option value="">
                  {siteOptionsLoading
                    ? 'Loading sites…'
                    : !form.org
                      ? 'Select an organization first'
                      : 'Please select a site'}
                </option>
                {siteOptions.map((site) => (
                  <option key={site.value} value={site.value}>
                    {site.label}
                  </option>
                ))}
              </select>
            </div>

            <fieldset className="network-scan-manager-field network-scan-manager-fieldset">
              <legend className="ghost-exposure-sys-field-label network-scan-manager-label">Scan type</legend>
              <div className="network-scan-manager-radios">
                <label className="network-scan-manager-radio">
                  <input
                    type="radio"
                    name="nsm-scan-type"
                    value="perimeter"
                    checked={form.scanType === 'perimeter'}
                    onChange={() => patch({ scanType: 'perimeter', scanner: '' })}
                    disabled={isEditMode || isSubmitting}
                  />
                  Perimeter
                </label>
                <label className="network-scan-manager-radio">
                  <input
                    type="radio"
                    name="nsm-scan-type"
                    value="on-premise"
                    checked={form.scanType === 'on-premise'}
                    onChange={() => patch({ scanType: 'on-premise', scanner: '' })}
                    disabled={isEditMode || isSubmitting}
                  />
                  On-Premise
                </label>
              </div>
            </fieldset>
            {perimeterPoolOnly ? (
              <div className="network-scan-manager-field">
                <span className="ghost-exposure-sys-field-label network-scan-manager-label">Scanner</span>
                <p className="network-scan-manager-pool-note" data-testid="nsm-perimeter-pool">
                  {scannerOptions[0].label}: perimeter scans are spread across the available perimeter scanners.
                </p>
              </div>
            ) : (
            <div className="network-scan-manager-field">
              <label className="ghost-exposure-sys-field-label network-scan-manager-label" htmlFor="nsm-scanner">
                Scanner
              </label>
              <select
                id="nsm-scanner"
                className="ghost-exposure-sys-input network-scan-manager-select"
                value={form.scanner}
                onChange={(e) => patch({ scanner: e.target.value })}
                disabled={scannerOptionsLoading || !form.site || isSubmitting || isEditMode}
              >
                <option value="">
                  {scannerOptionsLoading
                    ? 'Loading scanners…'
                    : !form.site
                      ? 'Select a site first'
                      : 'Please select a scanner'}
                </option>
                {scannerOptions.map((scanner) => (
                  <option key={scanner.value} value={scanner.value}>
                    {scanner.label}
                  </option>
                ))}
              </select>
            </div>
            )}
          </div>
        </section>

        {optionsError ? (
          <div className="profile-settings-message profile-settings-error" role="alert">
            {optionsError}
          </div>
        ) : null}
        {submitError ? (
          <div className="profile-settings-message profile-settings-error" role="alert">
            {submitError}
          </div>
        ) : null}
      </div>

      <div className="network-scan-manager-actions">
        <button
          type="submit"
          className="ghost-exposure-sys-btn ghost-exposure-sys-btn--primary network-scan-manager-submit"
          disabled={isSubmitting || (Boolean(optionsError) && orgOptions.length === 0)}
        >
          {isSubmitting ? (isEditMode ? 'Saving…' : 'Running…') : (isEditMode ? 'Save changes' : 'Run Scan')}
        </button>
      </div>
    </form>
  );
};

const WebApplicationScanManagerModalBody = ({
  onBack,
  onClose,
  onSubmit,
  showBack = true,
  timePickerTheme = 'dark',
  userTimezone = getPreferredTimezoneDefault(),
  orgQuery = null,
  mode = 'create',
  initialValues = null,
  title = null,
}) => {
  const isEditMode = mode === 'edit';
  const [orgOptions, setOrgOptions] = useState([]);
  const [orgOptionsLoading, setOrgOptionsLoading] = useState(false);
  const [siteOptions, setSiteOptions] = useState([]);
  const [siteOptionsLoading, setSiteOptionsLoading] = useState(false);
  const [scannerOptions, setScannerOptions] = useState([]);
  const [scannerOptionsLoading, setScannerOptionsLoading] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [optionsError, setOptionsError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [form, setForm] = useState(() => {
    const { startDate, startTime } = getNetworkScanScheduleDefaults(userTimezone);
    const defaults = {
      urls: '',
      startDate,
      startTime,
      timezone: String(userTimezone || getPreferredTimezoneDefault()),
      frequency: 'one-off',
      scheduleName: '',
      org: String(orgQuery?.org_id || '').trim(),
      site: '',
      scanner: '',
      egressMode: '',
      egressFallback: '',
      egressProxy: '',
      egressProxyOnce: null,
      egressRotation: '',
      requestDelayMs: '',
      scanLogin: null,
      scanLoginOnce: null,
    };
    const merged = { ...defaults, ...(initialValues || {}) };
    return {
      ...merged,
      scheduleName: sanitizeScheduleName(merged.scheduleName),
      urls: String(merged.urls ?? '').replace(/\0/g, ''),
    };
  });

  useEffect(() => {
    let mounted = true;
    setOrgOptionsLoading(true);
    setOptionsError('');
    listOrganizationsForSelector()
      .then((rows) => {
        if (!mounted) return;
        const list = Array.isArray(rows) ? rows : [];
        const normalized = list
          .map((item) => ({
            value: resolveOrgIdentity(item),
            label: String(item?.name || item?.org_name || '').trim() || 'Unnamed organization',
          }))
          .filter((item) => item.value);
        setOrgOptions(normalized);
        if (!normalized.length) {
          setOptionsError('No organizations are available for your account.');
        }
        setForm((f) => {
          if (isEditMode && f.org) return f;
          if (f.org && normalized.some((o) => o.value === f.org)) return f;
          const preferred = String(orgQuery?.org_id || '').trim();
          if (preferred && normalized.some((o) => o.value === preferred)) {
            return { ...f, org: preferred, site: '', scanner: '' };
          }
          if (normalized.length === 1) {
            return { ...f, org: normalized[0].value, site: '', scanner: '' };
          }
          return { ...f, org: '', site: '', scanner: '' };
        });
      })
      .catch((err) => {
        if (!mounted) return;
        setOrgOptions([]);
        setOptionsError(
          toUserFacingErrorMessage(err?.message, 'Unable to load organizations. Please try again.')
        );
      })
      .finally(() => {
        if (!mounted) return;
        setOrgOptionsLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, [isEditMode, orgQuery?.org_id]);

  useEffect(() => {
    // `org_uu_id` must be a UUID; never fall back to the integer pk.
    const orgUuid = isOrgUuid(form.org) ? String(form.org).trim() : '';
    if (!orgUuid) {
      setSiteOptions([]);
      setSiteOptionsLoading(false);
      setForm((f) => (isEditMode ? f : (f.site || f.scanner ? { ...f, site: '', scanner: '' } : f)));
      return;
    }

    let mounted = true;
    setSiteOptionsLoading(true);
    setOptionsError('');
    getSites(null, { org_uu_id: orgUuid })
      .then((rows) => {
        if (!mounted) return;
        const normalized = (Array.isArray(rows) ? rows : [])
          .map((item) => ({
            value: item?.uu_id || item?.site_uuid || item?.id || '',
            label: item?.site_name || item?.name || item?.site || 'Unnamed site',
          }))
          .filter((item) => item.value);
        setSiteOptions(normalized);
        setForm((f) => {
          if (isEditMode && f.site) return f;
          if (normalized.some((s) => s.value === f.site)) return f;
          if (!normalized.length) return { ...f, site: '', scanner: '' };
          return { ...f, site: normalized[0].value, scanner: '' };
        });
      })
      .catch((err) => {
        if (!mounted) return;
        setSiteOptions([]);
        setForm((f) => (isEditMode ? f : { ...f, site: '', scanner: '' }));
        setOptionsError(
          toUserFacingErrorMessage(err?.message, 'Unable to load sites for this organization.')
        );
      })
      .finally(() => {
        if (!mounted) return;
        setSiteOptionsLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, [form.org, isEditMode]);

  useEffect(() => {
    const siteUuid = String(form.site || '').trim();
    if (!siteUuid) {
      setScannerOptions([]);
      return;
    }
    let mounted = true;
    setScannerOptionsLoading(true);
    setOptionsError('');
    getWebScannersForSite(siteUuid)
      .then((rows) => {
        if (!mounted) return;
        const normalized = (Array.isArray(rows) ? rows : [])
          .map((item) => ({
            value: item?.setting_id || item?.id || '',
            label: item?.scanner_vanity_name || item?.scanner_name || item?.name || 'Unnamed scanner',
          }))
          .filter((item) => item.value);
        setScannerOptions(normalized);
        setForm((f) => ({
          ...f,
          scanner:
            isEditMode && f.scanner
              ? f.scanner
              : normalized.some((s) => s.value === f.scanner)
                ? f.scanner
                : normalized[0]?.value || '',
        }));
      })
      .catch((err) => {
        if (!mounted) return;
        setScannerOptions([]);
        setForm((f) => ({ ...f, scanner: '' }));
        setOptionsError(
          toUserFacingErrorMessage(err?.message, 'Unable to load scanners for this site.')
        );
      })
      .finally(() => {
        if (!mounted) return;
        setScannerOptionsLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, [form.site, isEditMode]);

  const patch = (partial) => setForm((f) => ({ ...f, ...partial }));

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setSubmitError('');
    if (isSubmitting) return;

    if (!String(form.org || '').trim()) {
      setSubmitError('Please select an organization.');
      return;
    }
    if (!String(form.site || '').trim()) {
      setSubmitError('Please select a site.');
      return;
    }
    // Organizations without dedicated web scanners run on the shared pool and
    // get no scanner picker; only dedicated-scanner customers choose one.
    if (scannerOptions.length > 0 && !String(form.scanner || '').trim()) {
      setSubmitError('Please select a scanner.');
      return;
    }

    const urlValidation = validateWebUrlsInput(form.urls);
    if (!urlValidation.valid) {
      const badPart = urlValidation.invalidEntry ? ` Invalid entry: ${urlValidation.invalidEntry}` : '';
      setSubmitError(`${urlValidation.message}${badPart}`);
      return;
    }

    const freq = String(form.frequency || 'one-off').trim().toLowerCase();
    const nameValidation = validateScheduleNameInput(form.scheduleName, { required: freq !== 'one-off' });
    if (!nameValidation.valid) {
      setSubmitError(nameValidation.message);
      return;
    }

    if (!normalizeDateString(String(form.startDate || '').trim())) {
      setSubmitError('Please select a valid start date.');
      return;
    }
    if (!normalizeTimeString(String(form.startTime || '').trim())) {
      setSubmitError('Please select a valid start time.');
      return;
    }
    if (form.egressMode === 'BYO') {
      if (form.egressProxyOnce) {
        const proxyProblem = proxyFieldsError(form.egressProxyOnce);
        if (proxyProblem) {
          setSubmitError(proxyProblem);
          return;
        }
        if (freq !== 'one-off') {
          setSubmitError('A proxy entered for one scan can’t be used by a schedule. Save it under Settings → Scan network first.');
          return;
        }
      } else if (!form.egressProxy) {
        setSubmitError('Choose your proxy, or enter one for this scan.');
        return;
      }
    }

    if (form.scanLoginOnce) {
      const loginProblem = scanLoginFieldsError(form.scanLoginOnce);
      if (loginProblem) {
        setSubmitError(loginProblem);
        return;
      }
    }

    const now = new Date();
    const datePart = normalizeDateString(String(form.startDate || '').trim() || toDateInputValue(now));
    const timePart = normalizeTimeString(String(form.startTime || '').trim() || toTimeInputValue(now));
    const payload = {
      ...form,
      urls: urlValidation.sanitized || String(form.urls || '').replace(/\0/g, '').trim(),
      scheduleName: nameValidation.sanitized,
      startDate: datePart,
      startTime: timePart,
      startDateTime: `${datePart}T${timePart}`,
      egressProxyOnce: form.egressMode === 'BYO' && form.egressProxyOnce ? proxyPayload(form.egressProxyOnce) : null,
      scanLoginOnce: form.scanLoginOnce ? scanLoginPayload(form.scanLoginOnce) : null,
    };

    setIsSubmitting(true);
    try {
      await onSubmit?.(payload);
    } catch (err) {
      setSubmitError(toUserFacingErrorMessage(err?.message, 'Failed to schedule web scan.'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form className="network-scan-manager network-scan-manager--cards" onSubmit={handleFormSubmit} noValidate>
      <div className={`network-scan-manager-top${showBack ? '' : ' network-scan-manager-top--no-back'}`}>
        {showBack ? (
          <button type="button" className="network-scan-manager-back" onClick={onBack} aria-label="Back">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </button>
        ) : null}
        <h2 id="web-scan-manager-title" className="ghost-cve-section-title network-scan-manager-title">
          {title || (isEditMode ? 'Edit Web Schedule' : 'Web Application Scan Manager')}
        </h2>
        <button type="button" className="new-scan-modal-close" aria-label="Close" onClick={onClose}>
          ✕
        </button>
      </div>

      <div className="network-scan-manager-body">
        <section className="settings-modal-card" aria-labelledby="wsm-targets-heading">
          <div className="settings-modal-card-head">
            <h3 id="wsm-targets-heading" className="settings-modal-card-title">
              Targets
            </h3>
            <p className="settings-modal-card-hint">
              Enter one or more application URLs to include in this scan.
            </p>
          </div>
          <div className="settings-modal-card-body">
            <div className="network-scan-manager-field">
              <label className="ghost-exposure-sys-field-label network-scan-manager-label" htmlFor="wsm-urls">
                URLs
              </label>
              <textarea
                id="wsm-urls"
                className="ghost-exposure-sys-input network-scan-manager-textarea"
                rows={4}
                placeholder="http://www.test.com"
                value={form.urls}
                onChange={(e) => patch({ urls: e.target.value })}
                autoComplete="off"
              />
            </div>
          </div>
        </section>

        <section className="settings-modal-card" aria-labelledby="wsm-schedule-heading">
          <div className="settings-modal-card-head">
            <h3 id="wsm-schedule-heading" className="settings-modal-card-title">
              Schedule
            </h3>
            <p className="settings-modal-card-hint">
              Choose when this scan should start and how often it should repeat.
            </p>
          </div>
          <div className="settings-modal-card-body">
            <div className="network-scan-manager-meta-row">
              <div className="network-scan-manager-field">
                <label className="ghost-exposure-sys-field-label network-scan-manager-label" htmlFor="wsm-start">
                  {form.frequency === 'one-off' ? 'Start date & time' : 'Recurring schedule'}
                </label>
                {form.frequency === 'daily' && (
                  <span className="settings-modal-card-hint network-scan-manager-freq-hint">
                    Scan will repeat every day at the selected time.
                  </span>
                )}
                {form.frequency === 'weekly' && (
                  <span className="settings-modal-card-hint network-scan-manager-freq-hint">
                    Scan will repeat every week on the selected weekday at the selected time.
                  </span>
                )}
                {form.frequency === 'fortnightly' && (
                  <span className="settings-modal-card-hint network-scan-manager-freq-hint">
                    Scan will repeat twice a month (about every 14 days) around the selected day and time.
                  </span>
                )}
                {form.frequency === 'monthly' && (
                  <span className="settings-modal-card-hint network-scan-manager-freq-hint">
                    Scan will repeat on day {form.startDate ? new Date(form.startDate + 'T00:00').getDate() : ''} of every month at the selected time.
                  </span>
                )}
                {form.frequency === 'quarterly' && (
                  <span className="settings-modal-card-hint network-scan-manager-freq-hint">
                    Scan will repeat every 3 months on the selected day and time.
                  </span>
                )}
                <div className="network-scan-manager-date-time-grid">
                  <div className="network-scan-manager-date-picker-wrap">
                    <div className="network-scan-manager-field-label-row">
                      <span className="network-scan-manager-time-field-label">
                        {form.frequency === 'one-off' ? 'Select date' : 'Select recurring day'}
                      </span>
                      <button
                        type="button"
                        className="network-scan-manager-inline-quick-action"
                        onClick={() => patch({ startDate: toDateInputValue(new Date()) })}
                      >
                        Today
                      </button>
                    </div>
                    <NetworkScanDatePicker
                      id="wsm-start"
                      value={form.startDate}
                      onChange={(next) => patch({ startDate: next })}
                      theme={timePickerTheme}
                    />
                  </div>
                  <div className="network-scan-manager-time-wrap">
                    <div className="network-scan-manager-field-label-row">
                      <span className="network-scan-manager-time-field-label">
                        {form.frequency === 'one-off' ? 'Select time' : 'Select recurring time'}
                      </span>
                      <button
                        type="button"
                        className="network-scan-manager-inline-quick-action"
                        onClick={() => patch({ startTime: toTimeInputValue(new Date()) })}
                      >
                        Now
                      </button>
                    </div>
                    <NetworkScanTimePicker
                      id="wsm-start-time"
                      value={form.startTime}
                      onChange={(next) => patch({ startTime: next })}
                      theme={timePickerTheme}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="network-scan-manager-field">
              <label className="ghost-exposure-sys-field-label network-scan-manager-label" htmlFor="wsm-frequency">
                Scan frequency
              </label>
              <select
                id="wsm-frequency"
                className="ghost-exposure-sys-input network-scan-manager-select"
                value={form.frequency}
                onChange={(e) => {
                  const v = e.target.value;
                  patch(v === 'one-off' ? { frequency: v, scheduleName: '' } : { frequency: v });
                }}
              >
                <option value="one-off">One-Off</option>
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
                <option value="fortnightly">Fortnightly</option>
                <option value="monthly">Monthly</option>
                <option value="quarterly">Quarterly</option>
              </select>
            </div>

            {form.frequency !== 'one-off' ? (
              <div className="network-scan-manager-field">
                <label className="ghost-exposure-sys-field-label network-scan-manager-label" htmlFor="wsm-schedule-name">
                  Schedule name
                </label>
                <input
                  id="wsm-schedule-name"
                  type="text"
                  className="ghost-exposure-sys-input network-scan-manager-input"
                  value={form.scheduleName}
                  onChange={(e) => patch({ scheduleName: e.target.value })}
                  autoComplete="off"
                />
              </div>
            ) : null}
          </div>
        </section>

        <section className="settings-modal-card" aria-labelledby="wsm-scope-heading">
          <div className="settings-modal-card-head">
            <h3 id="wsm-scope-heading" className="settings-modal-card-title">
              Scope &amp; scanner
            </h3>
            <p className="settings-modal-card-hint">
              {scannerOptions.length > 0
                ? 'Choose the organization, site, and scanner that will run this scan.'
                : 'Choose the organization and site for this scan.'}
            </p>
          </div>
          <div className="settings-modal-card-body">
            <div className="network-scan-manager-field">
              <label className="ghost-exposure-sys-field-label network-scan-manager-label" htmlFor="wsm-org">
                Organization
              </label>
              <select
                id="wsm-org"
                className="ghost-exposure-sys-input network-scan-manager-select"
                value={form.org}
                onChange={(e) => patch({ org: e.target.value, site: '', scanner: '', egressMode: '', egressFallback: '', egressProxy: '', egressProxyOnce: null })}
                disabled={orgOptionsLoading || isSubmitting || isEditMode}
              >
                <option value="">
                  {orgOptionsLoading ? 'Loading organizations…' : 'Please select an organization'}
                </option>
                {orgOptions.map((org) => (
                  <option key={org.value} value={org.value}>
                    {org.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="network-scan-manager-field">
              <label className="ghost-exposure-sys-field-label network-scan-manager-label" htmlFor="wsm-site">
                Site
              </label>
              <select
                id="wsm-site"
                className="ghost-exposure-sys-input network-scan-manager-select"
                value={form.site}
                onChange={(e) => patch({ site: e.target.value, scanner: '' })}
                disabled={siteOptionsLoading || !form.org || isSubmitting || isEditMode}
              >
                <option value="">
                  {siteOptionsLoading
                    ? 'Loading sites…'
                    : !form.org
                      ? 'Select an organization first'
                      : 'Please select a site'}
                </option>
                {siteOptions.map((site) => (
                  <option key={site.value} value={site.value}>
                    {site.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="network-scan-manager-field">
              <label className="ghost-exposure-sys-field-label network-scan-manager-label" htmlFor="wsm-scan-profile">
                Scan depth
              </label>
              <select
                id="wsm-scan-profile"
                className="ghost-exposure-sys-input network-scan-manager-select"
                value={form.scanProfile || 'standard'}
                onChange={(e) => patch({ scanProfile: e.target.value === 'deep' ? 'deep' : 'standard' })}
                disabled={isSubmitting || isEditMode}
              >
                <option value="standard">Standard</option>
                <option value="deep">Deep (JavaScript crawling, stronger attacks, takes 2-3x longer)</option>
              </select>
            </div>

            {/* Only organizations with dedicated web scanners choose one; everyone
                else runs on the shared pool and sees no scanner field at all. */}
            {scannerOptions.length === 0 ? null : (
            <div className="network-scan-manager-field">
              <label className="ghost-exposure-sys-field-label network-scan-manager-label" htmlFor="wsm-scanner">
                Scanner
              </label>
              <select
                id="wsm-scanner"
                className="ghost-exposure-sys-input network-scan-manager-select"
                value={form.scanner}
                onChange={(e) => patch({ scanner: e.target.value })}
                disabled={scannerOptionsLoading || !form.site || isSubmitting || isEditMode}
              >
                <option value="">
                  {scannerOptionsLoading
                    ? 'Loading scanners…'
                    : !form.site
                      ? 'Select a site first'
                      : 'Please select a scanner'}
                </option>
                {scannerOptions.map((scanner) => (
                  <option key={scanner.value} value={scanner.value}>
                    {scanner.label}
                  </option>
                ))}
              </select>
            </div>
            )}
          </div>
        </section>

        <ScanNetworkOptions
          orgId={form.org}
          value={{
            egressMode: form.egressMode, egressFallback: form.egressFallback, requestDelayMs: form.requestDelayMs,
            egressProxy: form.egressProxy, egressProxyOnce: form.egressProxyOnce, egressRotation: form.egressRotation,
          }}
          onChange={(next) => patch(next)}
          disabled={isSubmitting}
          isEditMode={isEditMode}
        />

        <ScanLoginOptions
          siteId={form.site}
          value={{ scanLogin: form.scanLogin, scanLoginOnce: form.scanLoginOnce }}
          onChange={(next) => patch(next)}
          disabled={isSubmitting}
          isEditMode={isEditMode}
          recurring={String(form.frequency || 'one-off') !== 'one-off'}
          egressMode={form.egressMode}
        />

        {optionsError ? (
          <div className="profile-settings-message profile-settings-error" role="alert">
            {optionsError}
          </div>
        ) : null}
        {submitError ? (
          <div className="profile-settings-message profile-settings-error" role="alert">
            {submitError}
          </div>
        ) : null}
      </div>

      <div className="network-scan-manager-actions">
        <button
          type="submit"
          className="ghost-exposure-sys-btn ghost-exposure-sys-btn--primary network-scan-manager-submit"
          disabled={isSubmitting || (Boolean(optionsError) && orgOptions.length === 0)}
        >
          {isSubmitting ? (isEditMode ? 'Saving…' : 'Running…') : (isEditMode ? 'Save changes' : 'Run Scan')}
        </button>
      </div>
    </form>
  );
};

// --- ScansPageContent ---

const ScansPageContent = ({
  initialScanTab,
  onInitialScanTabConsumed,
  initialScanFilter = 'all',
  onInitialScanFilterConsumed,
  initialScanView = 'runs',
  initialScanLocation = 'all',
  refreshKey = 0,
  orgQuery = null,
  headerSelectedOrgName = '',
  universalSearchQuery = '',
  onUniversalSearchQueryChange,
  isDarkMode = true,
  userProfile = EMPTY_USER_PROFILE,
} = {}) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const scansUrlFilters = parseScansFilterFromSearchParams(searchParams);
  const scansDisplayTimezone = String(userProfile?.timezone || '').trim() || getPreferredTimezoneDefault();
  // Prefer URL tab (same as location) so deep links / View all cannot lose to a default 'web' writeback.
  const [scansTab, setScansTab] = useState(() => {
    const fromUrl = String(scansUrlFilters.tab || '').trim().toLowerCase();
    if (fromUrl === 'web' || fromUrl === 'network') return fromUrl;
    if (initialScanTab === 'web' || initialScanTab === 'network') return initialScanTab;
    return 'web';
  });
  const [webScanRows, setWebScanRows] = useState(null);
  const [webScanError, setWebScanError] = useState('');
  const [webScanInfo, setWebScanInfo] = useState('');
  const [networkScanRows, setNetworkScanRows] = useState(undefined);
  const [networkScanError, setNetworkScanError] = useState('');
  const [networkScanInfo, setNetworkScanInfo] = useState('');
  const [scanActionToasts, setScanActionToasts] = useState([]);
  const [scanDeleteConfirm, setScanDeleteConfirm] = useState(null);
  const [scanDeleteBusy, setScanDeleteBusy] = useState(false);
  const [scheduleEdit, setScheduleEdit] = useState(null);
  const scanDeleteDescId = useId();
  const [scanSocketState, setScanSocketState] = useState('connecting');
  const [scansStatusFilter, setScansStatusFilter] = useState(() => {
    const next = String(initialScanFilter || scansUrlFilters.status || 'all').trim().toLowerCase();
    const view = String(initialScanView || scansUrlFilters.view || '').toLowerCase();
    if (view === 'schedules' || next === 'scheduled') return 'scheduled';
    return next || 'all';
  });
  const [scansOrgFilter, setScansOrgFilter] = useState(
    () => String(headerSelectedOrgName || '').trim() || 'all'
  );
  const [scansRecordView, setScansRecordView] = useState(() => {
    const next = String(initialScanView || scansUrlFilters.view || '').toLowerCase();
    // Web All Scans / Schedules tabs removed — legacy schedule view maps to status=scheduled.
    if (next === 'schedules' || next === 'in_progress') {
      return next === 'schedules' ? 'schedules' : 'runs';
    }
    return 'runs';
  });
  // Location is URL-driven (sidebar Network / Perimeter) so nav cannot race local state.
  const networkLocationFilter = useMemo(() => {
    const next = String(
      scansUrlFilters.location || initialScanLocation || 'onprem'
    ).trim().toLowerCase();
    return next === 'perimeter' ? 'perimeter' : 'onprem';
  }, [initialScanLocation, scansUrlFilters.location]);

  useEffect(() => {
    const onTourNetworkScope = (event) => {
      const scope = String(event?.detail?.scope || '').trim().toLowerCase();
      if (scope === 'scheduled') {
        setScansStatusFilter('scheduled');
        navigate(buildScansPathWithFilter({
          status: 'scheduled',
          tab: 'network',
          view: 'schedules',
          location: 'onprem',
        }), { replace: true });
        return;
      }
      if (scope === 'onprem' || scope === 'perimeter') {
        navigate(buildScansPathWithFilter({
          status: scansStatusFilter,
          tab: 'network',
          view: scansStatusFilter === 'scheduled' ? 'schedules' : 'runs',
          location: scope,
        }), { replace: true });
      }
    };
    window.addEventListener('xspectre:tour-network-scope', onTourNetworkScope);
    return () => window.removeEventListener('xspectre:tour-network-scope', onTourNetworkScope);
  }, [navigate, scansStatusFilter]);

  useEffect(() => {
    const fromUrl = String(scansUrlFilters.tab || '').trim().toLowerCase();
    if (fromUrl === 'web' || fromUrl === 'network') {
      setScansTab(fromUrl);
      return;
    }
    if (initialScanTab !== 'web' && initialScanTab !== 'network') return;
    setScansTab(initialScanTab);
    onInitialScanTabConsumed?.();
  }, [initialScanTab, onInitialScanTabConsumed, scansUrlFilters.tab]);

  useEffect(() => {
    const next = String(initialScanFilter || 'all').trim().toLowerCase();
    if (next === 'scheduled') {
      setScansStatusFilter('scheduled');
      setScansRecordView('schedules');
    } else {
      setScansStatusFilter(next || 'all');
      setScansRecordView('runs');
    }
    onInitialScanFilterConsumed?.();
  }, [initialScanFilter, onInitialScanFilterConsumed]);

  // Web All Scans / Schedules tabs removed — status filter drives schedule vs run lists.
  useEffect(() => {
    if (scansTab !== 'web') return;
    setScansRecordView(scansStatusFilter === 'scheduled' ? 'schedules' : 'runs');
  }, [scansTab, scansStatusFilter]);

  useEffect(() => {
    navigate(buildScansPathWithFilter({
      status: scansStatusFilter,
      tab: scansTab,
      view: scansStatusFilter === 'scheduled' ? 'schedules' : 'runs',
      location: scansTab === 'network' ? networkLocationFilter : null,
    }), { replace: true });
  }, [navigate, networkLocationFilter, scansStatusFilter, scansTab]);

  useEffect(() => {
    if (!scanActionToasts.length) return undefined;
    const timers = scanActionToasts
      .filter((toast) => toast.tone !== 'error')
      .map((toast) => window.setTimeout(() => {
        setScanActionToasts((prev) => prev.filter((item) => item.id !== toast.id));
      }, 5000));
    return () => timers.forEach((id) => window.clearTimeout(id));
  }, [scanActionToasts]);

  const pushScanActionToast = useCallback((message, tone = 'info') => {
    const text = String(message || '').trim();
    if (!text) return;
    const id = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    setScanActionToasts((prev) => [...prev.slice(-2), { id, message: text, tone }]);
  }, []);

  const dismissScanActionToast = useCallback((id) => {
    setScanActionToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  const closeScheduleEdit = useCallback(() => {
    setScheduleEdit(null);
  }, []);

  const openScheduleEdit = useCallback((row) => {
    const n = normalizeScansRow(row);
    const typeRaw = String(n.scanType || row?.scanType || row?.scan_type || row?.type || scansTab || '')
      .trim()
      .toLowerCase();
    const type = typeRaw.includes('network') ? 'network' : 'web';
    setScheduleEdit({ scanType: type, scheduleId: n.scanId, row });
  }, [scansTab]);

  useEffect(() => {
    if (typeof WebSocket === 'undefined') {
      setScanSocketState('disconnected');
      return undefined;
    }

    let socket = null;
    let reconnectTimer = null;
    let heartbeatTimer = null;
    let reconnectAttempt = 0;
    let isDisposed = false;

    const clearReconnectTimer = () => {
      if (reconnectTimer) {
        window.clearTimeout(reconnectTimer);
        reconnectTimer = null;
      }
    };

    const clearHeartbeatTimer = () => {
      if (heartbeatTimer) {
        window.clearInterval(heartbeatTimer);
        heartbeatTimer = null;
      }
    };

    const scheduleReconnect = () => {
      if (isDisposed) return;
      clearReconnectTimer();
      const delayMs = Math.min(30000, 1000 * (2 ** reconnectAttempt));
      reconnectAttempt += 1;
      reconnectTimer = window.setTimeout(() => {
        connect();
      }, delayMs);
    };

    const onMessage = (event) => {
      if (!event?.data) return;

      let payload;
      try {
        payload = JSON.parse(event.data);
      } catch {
        return;
      }

      if (payload?.event === 'scan_status_pong') return;
      if (payload?.event !== 'scan_status_update' || !payload?.row) return;
      const selectedSiteUuid = String(orgQuery?.site_uuid || orgQuery?.site_uu_id || '').trim();
      const eventSiteUuid = String(payload.row.site_uuid || '').trim();
      if (selectedSiteUuid && eventSiteUuid !== selectedSiteUuid) return;

      const scanType = String(payload.scanType || '').trim().toLowerCase();
      if (scanType === 'web') {
        setWebScanRows((currentRows) => {
          if (!Array.isArray(currentRows)) return currentRows;
          return upsertScanRowByIdentity(currentRows, payload.row);
        });
        return;
      }

      if (scanType === 'network') {
        setNetworkScanRows((currentRows) => {
          if (!Array.isArray(currentRows)) return currentRows;
          return upsertScanRowByIdentity(currentRows, payload.row);
        });
      }
    };

    const connect = async () => {
      if (isDisposed) return;
      setScanSocketState('connecting');

      try {
        await ensureAuthenticatedSession();
      } catch {
        // Keep reconnect behavior even when auth refresh fails.
      }

      const wsUrl = getScanStatusWebSocketUrl(orgQuery);
      if (!wsUrl) {
        scheduleReconnect();
        return;
      }

      try {
        socket = new WebSocket(wsUrl);
      } catch {
        setScanSocketState('disconnected');
        scheduleReconnect();
        return;
      }

      socket.onopen = () => {
        reconnectAttempt = 0;
        setScanSocketState('live');
        clearHeartbeatTimer();
        heartbeatTimer = window.setInterval(() => {
          if (!socket || socket.readyState !== WebSocket.OPEN) return;
          try {
            socket.send(JSON.stringify({ type: 'ping', ts: Date.now() }));
          } catch {
            // onclose/reconnect handles transport failures.
          }
        }, 20000);
      };

      socket.onmessage = onMessage;

      socket.onerror = () => {
        setScanSocketState('disconnected');
      };

      socket.onclose = () => {
        if (isDisposed) return;
        clearHeartbeatTimer();
        setScanSocketState('disconnected');
        scheduleReconnect();
      };
    };

    connect();

    return () => {
      isDisposed = true;
      setScanSocketState('disconnected');
      clearReconnectTimer();
      clearHeartbeatTimer();
      if (socket && (socket.readyState === WebSocket.OPEN || socket.readyState === WebSocket.CONNECTING)) {
        socket.close();
      }
    };
  }, [orgQuery]);

  const pollWhenSocketDown = scanSocketState !== 'live';
  const webScansQuery = useWebScansQuery({
    orgQuery,
    enabled: scansTab === 'web',
    refreshKey,
    refetchInterval: scansTab === 'web' && pollWhenSocketDown ? 12000 : false,
  });
  const networkScansQuery = useNetworkScansQuery({
    orgQuery,
    enabled: scansTab === 'network',
    refreshKey,
    refetchInterval: scansTab === 'network' && pollWhenSocketDown ? 12000 : false,
  });

  const handleScheduleEditSubmit = useCallback(async (payload) => {
    const scheduleId = String(scheduleEdit?.scheduleId || '').trim();
    if (!scheduleId) {
      throw new Error('Schedule ID is missing.');
    }
    if (scheduleEdit?.scanType === 'network') {
      await updateNetworkScanSchedule(payload, null, { uu_id: scheduleId });
      await networkScansQuery.refetch();
    } else {
      await updateWebApplicationScanSchedule(payload, null, { uu_id: scheduleId });
      await webScansQuery.refetch();
    }
    pushScanActionToast('Schedule updated successfully.', 'info');
    closeScheduleEdit();
  }, [closeScheduleEdit, networkScansQuery, pushScanActionToast, scheduleEdit, webScansQuery]);

  const handleRunScheduleNow = useCallback(async (row) => {
    const n = normalizeScansRow(row);
    const scheduleId = String(n.scanId || '').trim();
    if (!scheduleId) {
      pushScanActionToast('Unable to run scan: schedule ID is missing.', 'error');
      return;
    }
    const typeRaw = String(n.scanType || row?.scanType || row?.scan_type || row?.type || scansTab || '')
      .trim()
      .toLowerCase();
    const scanType = typeRaw.includes('network') ? 'network' : 'web';

    try {
      if (scanType === 'network') {
        const values = mapScheduleRowToNetworkInitialValues(row, scansDisplayTimezone);
        if (!String(values.targets || '').trim()) {
          throw new Error('This schedule has no target to scan.');
        }
        if (!String(values.site || '').trim() || !String(values.scanner || '').trim()) {
          throw new Error('This schedule is missing site or scanner details.');
        }
        await submitNetworkScanSchedule({
          ...values,
          frequency: 'one-off',
          startDate: '',
          startTime: '',
          scheduleName: '',
        });
        await networkScansQuery.refetch();
      } else {
        const values = mapScheduleRowToWebInitialValues(row, scansDisplayTimezone);
        if (!String(values.urls || '').trim()) {
          throw new Error('This schedule has no URL to scan.');
        }
        if (!String(values.site || '').trim() || !String(values.scanner || '').trim()) {
          throw new Error('This schedule is missing site or scanner details.');
        }
        await submitWebApplicationScanSchedule({
          ...values,
          frequency: 'one-off',
          startDate: '',
          startTime: '',
          scheduleName: '',
        });
        await webScansQuery.refetch();
      }
      pushScanActionToast('Scan started from this schedule.', 'info');
    } catch (err) {
      pushScanActionToast(
        toUserFacingErrorMessage(err?.message, 'Unable to start scan from this schedule.'),
        'error'
      );
      console.error('Schedule run-now failed:', err);
    }
  }, [
    networkScansQuery,
    pushScanActionToast,
    scansDisplayTimezone,
    scansTab,
    webScansQuery,
  ]);

  // Keep local row state so WebSocket upserts / deletes can patch without fighting the query cache.
  useEffect(() => {
    if (scansTab !== 'web') return;
    const waitingForFirstResult =
      webScansQuery.isLoading || (webScansQuery.isFetching && webScansQuery.data === undefined);
    if (waitingForFirstResult) {
      setWebScanRows(null);
      setWebScanError('');
      setWebScanInfo('');
      return;
    }
    if (webScansQuery.isError) {
      setWebScanRows([]);
      setWebScanError(
        toUserFacingErrorMessage(webScansQuery.error?.message, 'Failed to load web scans. Please try again.')
      );
      setWebScanInfo('');
      return;
    }
    if (webScansQuery.data) {
      setWebScanRows(Array.isArray(webScansQuery.data.rows) ? webScansQuery.data.rows : []);
      setWebScanError('');
      setWebScanInfo(String(webScansQuery.data.info || '').trim());
    }
  }, [
    scansTab,
    webScansQuery.isLoading,
    webScansQuery.isFetching,
    webScansQuery.isError,
    webScansQuery.error,
    webScansQuery.data,
    webScansQuery.dataUpdatedAt,
  ]);

  useEffect(() => {
    if (scansTab !== 'network') return;
    const waitingForFirstResult =
      networkScansQuery.isLoading || (networkScansQuery.isFetching && networkScansQuery.data === undefined);
    if (waitingForFirstResult) {
      setNetworkScanRows(null);
      setNetworkScanError('');
      setNetworkScanInfo('');
      return;
    }
    if (networkScansQuery.isError) {
      setNetworkScanRows([]);
      setNetworkScanError(
        toUserFacingErrorMessage(
          networkScansQuery.error?.message,
          'Failed to load network scans. Please try again.'
        )
      );
      setNetworkScanInfo('');
      return;
    }
    if (networkScansQuery.data) {
      setNetworkScanRows(Array.isArray(networkScansQuery.data.rows) ? networkScansQuery.data.rows : []);
      setNetworkScanInfo(String(networkScansQuery.data.info || '').trim());
      setNetworkScanError('');
    }
  }, [
    scansTab,
    networkScansQuery.isLoading,
    networkScansQuery.isFetching,
    networkScansQuery.isError,
    networkScansQuery.error,
    networkScansQuery.data,
    networkScansQuery.dataUpdatedAt,
  ]);

  const statusMatchesFilter = useCallback(
    (row) => {
      if (scansStatusFilter === 'all') return true;
      const normalized = normalizeScansRow(row);
      if (scansStatusFilter === 'scheduled') return isScanScheduleRecord(normalized);
      return getScansLifecycleKind(
        normalized.status,
        normalized.progressPercent,
        normalized.taskLifecycle
      ) === scansStatusFilter;
    },
    [scansStatusFilter]
  );

  const headerOrgFilterName = String(headerSelectedOrgName || '').trim();

  // Keep table org scoping aligned with the header organization selector.
  useEffect(() => {
    setScansOrgFilter(headerOrgFilterName || 'all');
  }, [headerOrgFilterName, orgQuery?.org_id]);

  const orgMatchesFilter = useCallback(
    (row) => {
      if (scansOrgFilter === 'all') return true;
      // Header org already scopes the API results — don't empty the table on name mismatch.
      if (orgQuery?.org_id && headerOrgFilterName && scansOrgFilter === headerOrgFilterName) {
        return true;
      }
      return getScanOrgFilterId(row) === scansOrgFilter;
    },
    [scansOrgFilter, orgQuery?.org_id, headerOrgFilterName]
  );

  const openScanDetail = useCallback(
    (row) => {
      const normalized = normalizeScansRow(row);
      const lifecycle = getScansLifecycleKind(normalized.status, normalized.progressPercent);
      if (lifecycle !== 'completed' && lifecycle !== 'failed' && lifecycle !== 'in_progress') return;
      const scanId = String(normalized.scanId || '').trim();
      if (!scanId) return;
      const scanType = scansTab === 'network' ? 'network' : 'web';
      const scanLocation = scanType === 'network'
        && (networkLocationFilter === 'perimeter' || networkLocationFilter === 'onprem')
        ? networkLocationFilter
        : null;
      navigate(scanDetailPath(scanId, scanType, scanLocation), {
        state: {
          scanRow: normalized,
          scanType,
          targetLabel: normalized.target || '',
          scanLocation,
        },
      });
    },
    [navigate, networkLocationFilter, scansTab]
  );

  const handleRerunScan = useCallback(async (scanId, scanType) => {
    const normalizedScanId = String(scanId || '').trim();
    if (!normalizedScanId) {
      pushScanActionToast('Unable to re-run scan: scan ID is missing.', 'error');
      return;
    }
    try {
      await rerunScan(normalizedScanId, scanType);
      pushScanActionToast('Scan re-run queued successfully.', 'info');
    } catch (err) {
      pushScanActionToast(
        toUserFacingErrorMessage(err?.message, 'Scan re-run failed.'),
        'error'
      );
      console.error('Scan re-run failed:', err);
    }
  }, [pushScanActionToast]);

  const handlePauseScan = useCallback(async (scanId, scannerId) => {
    const normalizedScanId = String(scanId || '').trim();
    if (!normalizedScanId) {
      pushScanActionToast('Unable to pause scan: scan ID is missing.', 'error');
      return;
    }
    try {
      await pauseNetworkScan(normalizedScanId, scannerId || null);
      pushScanActionToast('Scan pause requested successfully.', 'info');
    } catch (err) {
      pushScanActionToast(
        toUserFacingErrorMessage(err?.message, 'Scan pause failed.'),
        'error'
      );
    }
  }, [pushScanActionToast]);

  const handleStopScan = useCallback(async (scanId, scannerId) => {
    const normalizedScanId = String(scanId || '').trim();
    if (!normalizedScanId) {
      pushScanActionToast('Unable to stop scan: scan ID is missing.', 'error');
      return;
    }
    try {
      await haltNetworkScan(normalizedScanId, scannerId || null);
      pushScanActionToast('Scan stop requested successfully.', 'info');
    } catch (err) {
      pushScanActionToast(
        toUserFacingErrorMessage(err?.message, 'Scan stop failed.'),
        'error'
      );
      console.error('Scan stop failed:', err);
    }
  }, [pushScanActionToast]);

  const normalizeScanDeleteEntries = useCallback((scanEntries) => (
    (Array.isArray(scanEntries) ? scanEntries : [scanEntries])
      .map((entry) => {
        if (entry && typeof entry === 'object') {
          return {
            scanId: String(entry.scanId || entry.id || '').trim(),
            status: String(entry.status || '').trim(),
            taskLifecycle: String(entry.taskLifecycle || '').trim(),
            recordKind: String(entry.recordKind || '').trim().toLowerCase(),
            scanType: String(entry.scanType || scansTab || '').trim().toLowerCase(),
          };
        }
        return {
          scanId: String(entry || '').trim(),
          status: '',
          taskLifecycle: '',
          recordKind: '',
          scanType: String(scansTab || '').trim().toLowerCase(),
        };
      })
      .filter((entry) => Boolean(entry.scanId))
  ), [scansTab]);

  const requestDeleteScans = useCallback((scanEntries) => {
    const normalizedEntries = normalizeScanDeleteEntries(scanEntries);
    if (normalizedEntries.length === 0) {
      pushScanActionToast('No valid scans were selected for deletion.', 'error');
      return;
    }
    setScanDeleteConfirm({ entries: normalizedEntries });
  }, [normalizeScanDeleteEntries, pushScanActionToast]);

  const closeScanDeleteConfirm = useCallback(() => {
    if (scanDeleteBusy) return;
    setScanDeleteConfirm(null);
  }, [scanDeleteBusy]);

  const confirmDeleteScans = useCallback(async () => {
    const normalizedEntries = Array.isArray(scanDeleteConfirm?.entries) ? scanDeleteConfirm.entries : [];
    if (normalizedEntries.length === 0 || scanDeleteBusy) return;

    const scheduledIds = [];
    const regularIds = [];
    normalizedEntries.forEach((entry) => {
      const kind = getScansLifecycleKind(entry.status, null, entry.taskLifecycle);
      if ((entry.recordKind === 'schedule' || kind === 'scheduled') && entry.scanType === 'network') scheduledIds.push(entry.scanId);
      else regularIds.push(entry.scanId);
    });

    const scheduledDeletedIds = [];
    const scheduledFallbackIds = [];

    setScanDeleteBusy(true);
    try {
      if (scheduledIds.length > 0) {
        const scheduledResults = await Promise.all(
          [...new Set(scheduledIds)].map(async (scanId) => {
            try {
              await deleteNetworkScanSchedule({ uu_id: scanId });
              return { scanId, deleted: true, message: '' };
            } catch (err) {
              return {
                scanId,
                deleted: false,
                message: String(err?.message || ''),
              };
            }
          })
        );
        scheduledResults.forEach((result) => {
          if (result.deleted) {
            scheduledDeletedIds.push(result.scanId);
            return;
          }
          const message = String(result.message || '').toLowerCase();
          if (message.includes('not found')) {
            const failedId = String(result.scanId || '').trim();
            if (failedId) scheduledFallbackIds.push(failedId);
          }
        });
      }

      const regularDeleteIds = [...new Set([...regularIds, ...scheduledFallbackIds])];
      let regularDeletedCount = 0;
      if (regularDeleteIds.length > 0) {
        const result = await deleteNetworkScans(regularDeleteIds);
        regularDeletedCount = Number(result?.deleted || 0);
      }

      const deletedCount = scheduledDeletedIds.length + regularDeletedCount;
      const deletedIds = new Set([...scheduledDeletedIds, ...regularDeleteIds]);

      setNetworkScanRows((prev) => {
        if (!Array.isArray(prev)) return prev;
        return prev.filter((r) => {
          const normalized = normalizeScansRow(r);
          return !deletedIds.has(String(normalized.scanId || '').trim());
        });
      });
      setWebScanRows((prev) => {
        if (!Array.isArray(prev)) return prev;
        return prev.filter((r) => {
          const normalized = normalizeScansRow(r);
          return !deletedIds.has(String(normalized.scanId || '').trim());
        });
      });

      setScanDeleteConfirm(null);
      if (deletedCount > 0) {
        pushScanActionToast(`Deleted ${deletedCount} scan${deletedCount === 1 ? '' : 's'}.`, 'info');
      }
    } catch (err) {
      pushScanActionToast(
        toUserFacingErrorMessage(err?.message, 'Scan delete failed.'),
        'error'
      );
      console.error('Scan delete failed:', err);
    } finally {
      setScanDeleteBusy(false);
    }
  }, [pushScanActionToast, scanDeleteBusy, scanDeleteConfirm]);

  const webRowsForView = Array.isArray(webScanRows)
    ? webScanRows
    : (Array.isArray(webScansQuery.data?.rows) ? webScansQuery.data.rows : null);
  const webTableLoading =
    scansTab === 'web' &&
    webRowsForView == null &&
    (webScansQuery.isPending || webScansQuery.isFetching);
  const webTableData =
    scansTab === 'web' && Array.isArray(webRowsForView)
      ? webRowsForView
        .filter((row) => {
          if (scansStatusFilter === 'scheduled') return isScanScheduleRecord(row);
          return !isScanScheduleRecord(row);
        })
        .filter(statusMatchesFilter)
        .filter(orgMatchesFilter)
      : scansTab === 'web'
        ? webRowsForView
        : undefined;
  const networkRowsForView = Array.isArray(networkScanRows)
    ? networkScanRows
    : (Array.isArray(networkScansQuery.data?.rows) ? networkScansQuery.data.rows : null);
  const networkTableLoading =
    scansTab === 'network' &&
    networkRowsForView == null &&
    (networkScansQuery.isPending || networkScansQuery.isFetching);

  const networkScopedRows = useMemo(() => {
    if (scansTab !== 'network' || !Array.isArray(networkRowsForView)) return [];
    return networkRowsForView;
  }, [networkRowsForView, scansTab]);

  const matchesNetworkLocationScope = useCallback(
    (row) => {
      if (networkLocationFilter !== 'onprem' && networkLocationFilter !== 'perimeter') {
        return true;
      }
      return networkLocationKey(normalizeScansRow(row)) === networkLocationFilter;
    },
    [networkLocationFilter]
  );

  const networkTableData = useMemo(() => {
    let byScope = networkScopedRows;
    if (scansStatusFilter === 'scheduled') {
      // Scheduled tab is still location-scoped (on-prem vs perimeter).
      byScope = networkScopedRows.filter(
        (row) => isScanScheduleRecord(row) && matchesNetworkLocationScope(row)
      );
    } else if (networkLocationFilter === 'onprem' || networkLocationFilter === 'perimeter') {
      // On-prem / Perimeter run lists — schedules are available via status filter.
      byScope = networkScopedRows.filter(
        (row) => !isScanScheduleRecord(row) && matchesNetworkLocationScope(row)
      );
    }
    return byScope.filter(statusMatchesFilter).filter(orgMatchesFilter);
  }, [
    matchesNetworkLocationScope,
    networkLocationFilter,
    networkScopedRows,
    orgMatchesFilter,
    scansStatusFilter,
    statusMatchesFilter,
  ]);

  const buildScanViewTabs = useCallback((rows) => {
    const scoped = (Array.isArray(rows) ? rows : []).filter(orgMatchesFilter);
    const runs = scoped.filter((row) => !isScanScheduleRecord(row));
    let inProgress = 0;
    runs.forEach((row) => {
      const normalized = normalizeScansRow(row);
      if (
        getScansLifecycleKind(
          normalized.status,
          normalized.progressPercent,
          normalized.taskLifecycle
        ) === 'in_progress'
      ) {
        inProgress += 1;
      }
    });
    const scheduled = scoped.filter((row) => isScanScheduleRecord(row)).length;
    const activeId = scansStatusFilter === 'in_progress' || scansStatusFilter === 'scheduled'
      ? scansStatusFilter
      : 'all';
    return {
      activeId,
      tabs: [
        { id: 'all', label: 'All scans', count: runs.length },
        { id: 'in_progress', label: 'In progress', count: inProgress },
        { id: 'scheduled', label: 'Scheduled', count: scheduled },
      ],
    };
  }, [orgMatchesFilter, scansStatusFilter]);

  const webViewTabs = useMemo(
    () => buildScanViewTabs(Array.isArray(webRowsForView) ? webRowsForView : []),
    [buildScanViewTabs, webRowsForView]
  );

  const networkViewTabs = useMemo(() => {
    const scoped = networkScopedRows.filter(orgMatchesFilter);
    const locationRuns = scoped.filter(
      (row) => !isScanScheduleRecord(row) && matchesNetworkLocationScope(row)
    );
    const scheduled = scoped.filter(
      (row) => isScanScheduleRecord(row) && matchesNetworkLocationScope(row)
    ).length;
    let inProgress = 0;
    locationRuns.forEach((row) => {
      const normalized = normalizeScansRow(row);
      if (
        getScansLifecycleKind(
          normalized.status,
          normalized.progressPercent,
          normalized.taskLifecycle
        ) === 'in_progress'
      ) {
        inProgress += 1;
      }
    });
    const activeId = scansStatusFilter === 'in_progress' || scansStatusFilter === 'scheduled'
      ? scansStatusFilter
      : 'all';
    return {
      activeId,
      tabs: [
        { id: 'all', label: 'All scans', count: locationRuns.length },
        { id: 'in_progress', label: 'In progress', count: inProgress },
        { id: 'scheduled', label: 'Scheduled', count: scheduled },
      ],
    };
  }, [
    matchesNetworkLocationScope,
    networkScopedRows,
    orgMatchesFilter,
    scansStatusFilter,
  ]);

  if (webTableLoading || networkTableLoading) {
    return (
      <div className="scans-page" data-tour="scans-page">
        <PageDataLoader label="Loading scans" />
      </div>
    );
  }

  return (
    <div className="scans-page" data-tour="scans-page">
      {scanDeleteConfirm ? (
        <div
          className="assets-detail-modal-overlay assets-exclude-confirm-overlay"
          role="presentation"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) closeScanDeleteConfirm();
          }}
        >
          <div
            className="assets-exclude-confirm-dialog"
            role="alertdialog"
            aria-modal="true"
            aria-label={
              scanDeleteConfirm.entries.every((entry) => entry.recordKind === 'schedule')
                ? 'Confirm delete schedule'
                : 'Confirm delete scan run'
            }
            aria-describedby={scanDeleteDescId}
          >
            <button
              type="button"
              className="assets-exclude-confirm-x"
              onClick={closeScanDeleteConfirm}
              aria-label="Close"
              disabled={scanDeleteBusy}
            >
              ✕
            </button>
            <p id={scanDeleteDescId} className="assets-exclude-confirm-desc">
              {scanDeleteConfirm.entries.every((entry) => entry.recordKind === 'schedule')
                ? scanDeleteConfirm.entries.length === 1
                  ? 'You are about to permanently delete this schedule. Historical scan runs are not deleted.'
                  : `You are about to permanently delete ${scanDeleteConfirm.entries.length} schedules. Historical scan runs are not deleted.`
                : scanDeleteConfirm.entries.length === 1
                  ? 'You are about to permanently delete this historical scan run. Any related schedule is not deleted.'
                  : `You are about to permanently delete ${scanDeleteConfirm.entries.length} historical scan runs. Related schedules are not deleted.`}
              <span className="assets-exclude-confirm-warning">This action cannot be undone.</span>
            </p>
            <div className="assets-exclude-confirm-actions">
              <button
                type="button"
                className="assets-exclude-confirm-btn assets-exclude-confirm-btn--danger"
                onClick={confirmDeleteScans}
                disabled={scanDeleteBusy}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {scheduleEdit ? (
        <div
          className="new-scan-modal-overlay"
          role="presentation"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) closeScheduleEdit();
          }}
        >
          <div
            className={`new-scan-modal new-scan-modal--steps${
              scheduleEdit.scanType === 'network' ? ' new-scan-modal--steps-network' : ''
            }`}
            role="dialog"
            aria-modal="true"
          >
            {scheduleEdit.scanType === 'network' ? (
              <NetworkScanManagerModalBody
                mode="edit"
                initialValues={mapScheduleRowToNetworkInitialValues(scheduleEdit.row, scansDisplayTimezone)}
                showBack={false}
                onBack={closeScheduleEdit}
                onClose={closeScheduleEdit}
                onSubmit={handleScheduleEditSubmit}
                userTimezone={scansDisplayTimezone}
                orgQuery={orgQuery}
                timePickerTheme={isDarkMode ? 'dark' : 'light'}
              />
            ) : (
              <WebApplicationScanManagerModalBody
                mode="edit"
                initialValues={mapScheduleRowToWebInitialValues(scheduleEdit.row, scansDisplayTimezone)}
                showBack={false}
                onBack={closeScheduleEdit}
                onClose={closeScheduleEdit}
                onSubmit={handleScheduleEditSubmit}
                userTimezone={scansDisplayTimezone}
                orgQuery={orgQuery}
                timePickerTheme={isDarkMode ? 'dark' : 'light'}
              />
            )}
          </div>
        </div>
      ) : null}

      {scanActionToasts.length > 0 ? (
        <div className="scans-action-toasts" aria-live="polite" aria-atomic="false">
          {scanActionToasts.map((toast) => (
            <div
              key={toast.id}
              className={`scans-action-toast ${toast.tone === 'error' ? 'scans-action-toast--error' : 'scans-action-toast--info'}`}
              role={toast.tone === 'error' ? 'alert' : 'status'}
            >
              <span>{toast.message}</span>
              <button
                type="button"
                className="scans-action-toast-dismiss"
                aria-label="Dismiss notification"
                onClick={() => dismissScanActionToast(toast.id)}
              >
                ✕
              </button>
            </div>
          ))}
        </div>
      ) : null}
      {/* Tab switcher removed — Web / Network are separate sidebar subnav pages. */}
      {scansTab === 'web' ? (
      <div
        id="scans-panel-web"
        className="scans-tab-panel"
      >
        <section className="assets-section">
          {webScanError ? (
            <div className="profile-settings-message profile-settings-error" role="alert">
              {webScanError}
            </div>
          ) : null}
          {!webScanError && webScanInfo ? (
            <div className="profile-settings-message profile-settings-info" role="status">
              {webScanInfo}
            </div>
          ) : null}
          <div
            className="scans-network-scope-tabs"
            role="tablist"
            aria-label="Web scan view"
            data-tour="scans-web-view-tabs"
          >
            <div className="scans-network-scope-tabs-track">
              {webViewTabs.tabs.map((tab) => {
                const active = webViewTabs.activeId === tab.id;
                return (
                  <button
                    key={tab.id}
                    type="button"
                    role="tab"
                    id={`scans-web-tab-${tab.id}`}
                    data-tour={`scans-web-tab-${tab.id}`}
                    aria-selected={active}
                    className={`scans-network-scope-tab${active ? ' is-active' : ''}`}
                    onClick={() => setScansStatusFilter(tab.id)}
                  >
                    <span className="scans-network-scope-tab-main">
                      <span className="scans-network-scope-tab-label">{tab.label}</span>
                    </span>
                    <span className="scans-network-scope-tab-count">{tab.count}</span>
                  </button>
                );
              })}
            </div>
          </div>
          <ScanTable
            key={`web-scans-${scansStatusFilter}`}
            scanType="web"
            data={webTableData}
            isLoading={webTableLoading}
            displayTimezone={scansDisplayTimezone}
            recordView={scansStatusFilter === 'scheduled' ? 'schedules' : 'runs'}
            itemLabel={scansStatusFilter === 'scheduled' ? 'schedules' : 'scan runs'}
            loadFailed={Boolean(webScanError)}
            emptyMessage={webScanError ? 'Unable to load web scans.' : ''}
            toolbarSearch
            toolbarSearchPlaceholder="Search web scans by name, target, organization, or site"
            universalSearchQuery={universalSearchQuery}
            onUniversalSearchQueryChange={onUniversalSearchQueryChange}
            onTargetClick={openScanDetail}
            onRerunScan={scansStatusFilter !== 'scheduled' ? handleRerunScan : null}
            onDeleteScan={requestDeleteScans}
            onEditSchedule={openScheduleEdit}
            onRunSchedule={scansStatusFilter === 'scheduled' ? handleRunScheduleNow : null}
            scanSocketState={scanSocketState}
          />
        </section>
      </div>
      ) : (
      <div
        id="scans-panel-network"
        className="scans-tab-panel"
      >
        <section className="assets-section">
          {networkScanError ? (
            <div className="profile-settings-message profile-settings-error" role="alert">
              {networkScanError}
            </div>
          ) : null}
          {!networkScanError && networkScanInfo ? (
            <div className="profile-settings-message profile-settings-info" role="status">
              {networkScanInfo}
            </div>
          ) : null}
          <div data-tour={`scans-network-panel-${networkLocationFilter}`}>
          <div
            className="scans-network-scope-tabs"
            role="tablist"
            aria-label="Network scan view"
            data-tour="scans-network-view-tabs"
          >
            <div className="scans-network-scope-tabs-track">
              {networkViewTabs.tabs.map((tab) => {
                const active = networkViewTabs.activeId === tab.id;
                return (
                  <button
                    key={tab.id}
                    type="button"
                    role="tab"
                    id={`scans-network-tab-${tab.id}`}
                    data-tour={`scans-network-tab-${tab.id}`}
                    aria-selected={active}
                    className={`scans-network-scope-tab${active ? ' is-active' : ''}`}
                    onClick={() => setScansStatusFilter(tab.id)}
                  >
                    <span className="scans-network-scope-tab-main">
                      <span className="scans-network-scope-tab-label">{tab.label}</span>
                    </span>
                    <span className="scans-network-scope-tab-count">{tab.count}</span>
                  </button>
                );
              })}
            </div>
          </div>
          <ScanTable
            key={`network-scans-${networkLocationFilter}-${scansStatusFilter}`}
            scanType="network"
            data={networkTableData}
            isLoading={networkTableLoading}
            displayTimezone={scansDisplayTimezone}
            recordView={scansStatusFilter === 'scheduled' ? 'schedules' : 'runs'}
            itemLabel={scansStatusFilter === 'scheduled' ? 'schedules' : 'scan runs'}
            loadFailed={Boolean(networkScanError)}
            emptyMessage={networkScanError ? 'Unable to load network scans.' : ''}
            hideNetworkTypeColumn={
              networkLocationFilter === 'onprem' || networkLocationFilter === 'perimeter'
            }
            toolbarSearch
            toolbarSearchPlaceholder="Search network scans by name, target, organization, or site"
            universalSearchQuery={universalSearchQuery}
            onUniversalSearchQueryChange={onUniversalSearchQueryChange}
            onTargetClick={openScanDetail}
            onRerunScan={scansStatusFilter !== 'scheduled' ? handleRerunScan : null}
            onStopScan={handleStopScan}
            onPauseScan={handlePauseScan}
            onDeleteScan={requestDeleteScans}
            onEditSchedule={openScheduleEdit}
            onRunSchedule={
              scansStatusFilter === 'scheduled' ? handleRunScheduleNow : null
            }
            scanSocketState={scanSocketState}
          />
          </div>
        </section>
      </div>
      )}
    </div>
  );
};

export { NetworkScanManagerModalBody, WebApplicationScanManagerModalBody };
export default ScansPageContent;
